import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { CircularProgress } from '@material-ui/core';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { compose } from 'redux';
import { formatMoney } from '../../util/currency';
import { types as sdkTypes } from '../../util/sdkLoader';
import { createSlug } from '../../util/urlHelpers';
import { ReloveIconFavorite, NamedLink, Avatar } from '../../components';
import { createResourceLocatorString } from '../../util/routes';
import routeConfiguration from '../../routeConfiguration';
import css from './ListingCardAlt.css';

const { Money } = sdkTypes;

const ListingCardAlt = props => {
  const { 
    listing, 
    className, 
    intl, 
    currentUser, 
    onToggleLike, 
    likeInProgress, 
    history, 
    location,
    handleLoginRequest
  } = props;
  const classes = classNames(css.root, 'listingCard', className);

  const {
    id,
    name: title,
    brand,
    price,
    original_price,
    image,
    size,
    sellerName,
    sellerProfileImage,
    likes,
    likedBy = [],
    urlSeller,
    brandUrl,
  } = listing;

  const formattedPrice = formatMoney(intl, new Money(price * 100, 'MXN'));
  const formattedOriginalPrice = original_price ? formatMoney(intl, new Money(original_price * 100, 'MXN')) : null;

  // Extract seller ID from the full URL
  const sellerId = urlSeller ? urlSeller.split('/u/')[1] : '';
  
  // Create slug from title
  const slug = createSlug(title);

  // Create seller object for Avatar component
  const seller = {
    attributes: {
      profile: {
        displayName: sellerName,
        abbreviatedName: sellerName
          .split(' ')
          .map(n => n[0])
          .join('')
          .substring(0, 2)
          .toUpperCase(),
        // Only include imageUrl if sellerProfileImage exists
        ...(sellerProfileImage && { imageUrl: sellerProfileImage })
      }
    }
  };

  // Handle toggle like functionality
  const handleToggleLike = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!!currentUser && !likeInProgress) {
      // Get current user ID
      const userId = currentUser.id.uuid;
      // Call the API to toggle like
      onToggleLike(userId, id);
    } else if (!currentUser) {
      if(handleLoginRequest){
        handleLoginRequest(id);
      } else {
        const state = { from: `${location.pathname}${location.search}${location.hash}` };
        history.push(createResourceLocatorString('SignupPage', routeConfiguration(), {}, {}), state);
      }
    }
  };

  // Check if the current user has liked this item
  const isLikedByCurrentUser = currentUser && 
    Array.isArray(likedBy) && 
    likedBy.includes(currentUser.id.uuid);
  
  const likesCount = likes || (likedBy && likedBy.length) || 0;
  const isCurrentLikeInProgress = !!likeInProgress && likeInProgress === id;

  const getInitials = (name) => {
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .substring(0, 2)
      .toUpperCase();
  };

  return (
    <div className={classes}>
      <NamedLink 
        name="ListingPage" 
        params={{ slug, id }} 
        className={css.imageLink}
      >
        <div className={css.imageWrapper}>
          <div className={css.aspectWrapper}>
            <img 
              className={css.rootForImage} 
              src={image} 
              alt={title}
              draggable="false"
            />
          </div>
        </div>
      </NamedLink>
      <div className={css.info}>
        <div 
          className={classNames(css.likeCount, isLikedByCurrentUser ? css.liked : null)}
          onClick={handleToggleLike}
        >
          <ReloveIconFavorite className={css.favoriteIcon} />
          {!isCurrentLikeInProgress ? (
            <span>{likesCount}</span>
          ) : (
            <CircularProgress className={css.favoriteLoader} color={'inherit'} size={14} />
          )}
        </div>
        <NamedLink 
          name="SearchPage"
          to={{ search: `?pub_brand=${encodeURIComponent(brand)}` }}
          className={css.brandLink}
          onClick={(e) => e.stopPropagation()}
          draggable="false"
        >
          {brand}
        </NamedLink>
        <div className={css.size}>
          Talla: <span className={css.allCaps}>{size}</span>
        </div>
        <div className={css.price}>
          <div className={css.priceValue} title={formattedPrice}>
            {formattedPrice}
          </div>
          {formattedOriginalPrice && (
            <div className={css.originalPriceValue} title={formattedOriginalPrice}>
              {formattedOriginalPrice}
            </div>
          )}
        </div>
        <div className={css.authorInfo}>
          <NamedLink 
            name="ProfilePage"
            params={{ id: sellerId }}
            className={css.sellerLink}
            onClick={(e) => e.stopPropagation()}
            draggable="false"
          >
            <div className={css.avatar}>
              {sellerProfileImage ? (
                <img 
                  src={sellerProfileImage} 
                  alt={sellerName}
                  draggable="false"
                  className={css.avatarImage}
                />
              ) : (
                <div className={css.avatarInitials}>
                  {getInitials(sellerName)}
                </div>
              )}
            </div>
            <span className={css.sellerName}>{sellerName}</span>
          </NamedLink>
        </div>
      </div>
    </div>
  );
};

ListingCardAlt.defaultProps = {
  className: null,
};

ListingCardAlt.propTypes = {
  listing: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    brand: PropTypes.string.isRequired,
    price: PropTypes.number.isRequired,
    original_price: PropTypes.number,
    image: PropTypes.string.isRequired,
    size: PropTypes.string.isRequired,
    sellerName: PropTypes.string.isRequired,
    sellerProfileImage: PropTypes.string,
    likes: PropTypes.number,
    likedBy: PropTypes.array,
    urlSeller: PropTypes.string.isRequired,
    brandUrl: PropTypes.string.isRequired,
  }).isRequired,
  className: PropTypes.string,
  intl: PropTypes.object.isRequired,
  currentUser: PropTypes.object,
  onToggleLike: PropTypes.func,
  likeInProgress: PropTypes.string,
  handleLoginRequest: PropTypes.func,
};

const mapStateToProps = state => {
  // Get likeInProgress from either LandingPage or ListingPage, depending on which is active
  const { likeInProgress: landingPageLikeInProgress } = state.LandingPage || {};
  const { likeInProgress: listingPageLikeInProgress } = state.ListingPage || {};
  const likeInProgress = landingPageLikeInProgress || listingPageLikeInProgress;
  
  const { currentUser } = state.user;
  return {
    currentUser,
    likeInProgress,
  };
};

export default compose(
  withRouter,
  connect(mapStateToProps)
)(ListingCardAlt); 